/**
 * OrganiserScoreCorrection — inline score correction for organisers with Master password unlock.
 *
 * Shows a group/team list → per-hole edit grid for any group.
 * Only visible when the Master password has been verified for this round.
 */
import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Minus, Plus, ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import { patchStrokes } from '../lib/api';
import { toast } from 'sonner';
import { groupDisplayName } from '../lib/groupOrder';
import { formatPlayingHandicap, getEffectivePH, shotsOnHole, effectiveStrokeIndexes } from '../golf';
import { effectiveCourseForPlayer } from '../lib/effectiveCourse';

export default function OrganiserScoreCorrection({ trip, round, code, roundId, onRefresh }) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [pendingEdits, setPendingEdits] = useState({}); // { "hole_idx": newValue }
  const [saving, setSaving] = useState(false);

  const teams = round?.teams || [];
  const players = trip?.players || [];
  const holePars = round?.hole_pars || [];
  const isScramble = round?.scoring_mode === 'texas_scramble';
  const si = effectiveStrokeIndexes(round);

  const handleGroupSelect = (idx) => {
    setSelectedGroup(idx);
    setPendingEdits({});
  };

  const getGroupPlayers = (groupIdx) => {
    return (teams[groupIdx] || []).map(pid => players.find(p => p.id === pid)).filter(Boolean);
  };

  // For scramble: team strokes. For individual: per-player strokes.
  const getHoleScore = (groupIdx, hole, playerId) => {
    if (isScramble) {
      const tk = String(groupIdx);
      return round?.team_hole_strokes?.[tk]?.[hole] ?? 0;
    }
    return round?.hole_strokes?.[playerId]?.[hole] ?? 0;
  };

  const handleAdjust = (key, delta) => {
    const current = pendingEdits[key];
    if (current !== undefined) {
      setPendingEdits(prev => ({ ...prev, [key]: Math.max(0, current + delta) }));
    }
  };

  const startEdit = (key, currentVal) => {
    if (pendingEdits[key] !== undefined) return; // already editing
    setPendingEdits(prev => ({ ...prev, [key]: currentVal }));
  };

  const revertEdit = (key) => {
    setPendingEdits(prev => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  };

  const handleSave = async () => {
    const edits = Object.entries(pendingEdits);
    if (edits.length === 0) return;
    setSaving(true);
    try {
      for (const [key, newVal] of edits) {
        const [type, ...rest] = key.split(':');
        if (type === 'team') {
          const [holeStr] = rest;
          const hole = parseInt(holeStr);
          // For scramble, send team_index + a player from the team as playerId
          const teamPids = teams[selectedGroup] || [];
          const pid = teamPids[0] || '';
          await patchStrokes(code, roundId, {
            playerId: pid,
            hole,
            value: newVal,
            team_index: selectedGroup,
          });
        } else {
          // Individual: type === playerId
          const pid = type;
          const hole = parseInt(rest[0]);
          const par = holePars[hole] ?? 4;
          const player = players.find(p => p.id === pid);
          const course = player ? effectiveCourseForPlayer(round, pid) : null;
          const ph = player ? getEffectivePH(player, course) : 0;
          const received = shotsOnHole(ph, si[hole] ?? 1);
          const stab = Math.max(0, 2 + par + received - newVal);
          await patchStrokes(code, roundId, {
            playerId: pid,
            hole,
            value: newVal,
            stableford: stab,
          });
        }
      }
      toast.success(`${edits.length} correction${edits.length > 1 ? 's' : ''} saved`);
      setPendingEdits({});
      if (onRefresh) onRefresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save correction');
    } finally {
      setSaving(false);
    }
  };

  const hasEdits = Object.keys(pendingEdits).length > 0;

  // ── Group list view ──
  if (selectedGroup === null) {
    return (
      <Card className="border-[#E1E6E4] rounded-xl overflow-hidden" data-testid="correction-group-list">
        <div className="bg-[#FFF8E1] px-3 py-2 border-b border-[#FFE0B2]">
          <p className="text-xs font-semibold text-[#E59000]">Correct scores (Master password active)</p>
        </div>
        <div className="divide-y divide-[#E1E6E4]">
          {teams.map((teamPids, idx) => {
            const gPlayers = getGroupPlayers(idx);
            return (
              <button key={idx} onClick={() => handleGroupSelect(idx)}
                className="w-full text-left px-3 py-3 hover:bg-[#F4F6F5] transition-colors flex items-center justify-between"
                data-testid={`correction-group-${idx}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1D242B]">{groupDisplayName(round, idx)}</p>
                  <p className="text-[10px] text-[#455561] break-words">{gPlayers.map(p => p.name).join(' · ')}</p>
                </div>
                <span className="text-xs text-[#B8C7C0] ml-2 flex-shrink-0">&rsaquo;</span>
              </button>
            );
          })}
        </div>
      </Card>
    );
  }

  // ── Per-hole edit grid for selected group ──
  const groupPlayers = getGroupPlayers(selectedGroup);

  return (
    <Card className="border-[#E1E6E4] rounded-xl overflow-hidden" data-testid="correction-hole-grid">
      <div className="bg-[#FFF8E1] px-3 py-2 border-b border-[#FFE0B2] flex items-center gap-2">
        <button onClick={() => { setSelectedGroup(null); setPendingEdits({}); }}
          className="text-[#E59000] p-0.5" data-testid="correction-back">
          <ArrowLeft className="w-4 h-4" weight="bold" />
        </button>
        <p className="text-xs font-semibold text-[#E59000] flex-1">{groupDisplayName(round, selectedGroup)} — Edit scores</p>
        {hasEdits && (
          <Button size="sm" onClick={handleSave} disabled={saving}
            className="bg-[#1B4332] hover:bg-[#163B2B] text-white text-xs h-7 px-3 rounded-lg"
            data-testid="correction-save">
            <FloppyDisk className="w-3 h-3 mr-1" weight="bold" />
            {saving ? 'Saving...' : `Save ${Object.keys(pendingEdits).length}`}
          </Button>
        )}
      </div>

      {isScramble ? (
        /* Scramble: one score per hole */
        <div className="divide-y divide-[#E1E6E4]">
          {Array.from({ length: 18 }, (_, h) => {
            const key = `team:${h}`;
            const current = getHoleScore(selectedGroup, h);
            const par = holePars[h] ?? 4;
            const isEditing = pendingEdits[key] !== undefined;
            const displayVal = isEditing ? pendingEdits[key] : current;
            const changed = isEditing && pendingEdits[key] !== current;
            return (
              <div key={h} className="flex items-center px-3 py-1.5 gap-2" data-testid={`correction-hole-${h}`}>
                <span className="w-6 text-xs font-bold text-[#455561] text-center">{h + 1}</span>
                <span className="text-[10px] text-[#B8C7C0] w-8 text-center">Par {par}</span>
                <div className="flex items-center gap-1 ml-auto">
                  {isEditing ? (
                    <>
                      <button onClick={() => handleAdjust(key, -1)} disabled={displayVal <= 0}
                        className="w-7 h-7 rounded-lg bg-[#F4F6F5] flex items-center justify-center hover:bg-[#E1E6E4] disabled:opacity-30">
                        <Minus className="w-3 h-3" weight="bold" />
                      </button>
                      <span className={`w-8 text-center text-sm font-bold tabular-nums ${changed ? 'text-[#E59000]' : 'text-[#1D242B]'}`}>{displayVal}</span>
                      <button onClick={() => handleAdjust(key, 1)}
                        className="w-7 h-7 rounded-lg bg-[#F4F6F5] flex items-center justify-center hover:bg-[#E1E6E4]">
                        <Plus className="w-3 h-3" weight="bold" />
                      </button>
                      {changed && (
                        <button onClick={() => revertEdit(key)} className="text-[9px] text-[#B8C7C0] hover:text-[#455561] underline ml-1">revert</button>
                      )}
                    </>
                  ) : (
                    <button onClick={() => startEdit(key, current)}
                      className="text-sm font-bold text-[#1D242B] tabular-nums hover:text-[#E59000] transition-colors px-2" data-testid={`correction-val-${h}`}>
                      {current || '–'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Individual: one section per player */
        <div>
          {groupPlayers.map(player => {
            const ph = getEffectivePH(player, effectiveCourseForPlayer(round, player.id));
            return (
              <div key={player.id} className="border-b border-[#E1E6E4] last:border-0">
                <div className="px-3 py-2 bg-[#F4F6F5]">
                  <span className="text-xs font-semibold text-[#1D242B]">{player.name}</span>
                  <span className="text-[10px] text-[#8A9BA3] ml-2">PH {formatPlayingHandicap(ph)}</span>
                </div>
                <div className="divide-y divide-[#F4F6F5]">
                  {Array.from({ length: 18 }, (_, h) => {
                    const key = `${player.id}:${h}`;
                    const current = getHoleScore(selectedGroup, h, player.id);
                    const par = holePars[h] ?? 4;
                    const isEditing = pendingEdits[key] !== undefined;
                    const displayVal = isEditing ? pendingEdits[key] : current;
                    const changed = isEditing && pendingEdits[key] !== current;
                    return (
                      <div key={h} className="flex items-center px-3 py-1 gap-2" data-testid={`correction-${player.id}-hole-${h}`}>
                        <span className="w-6 text-[10px] font-bold text-[#455561] text-center">{h + 1}</span>
                        <span className="text-[9px] text-[#B8C7C0] w-7 text-center">P{par}</span>
                        <div className="flex items-center gap-1 ml-auto">
                          {isEditing ? (
                            <>
                              <button onClick={() => handleAdjust(key, -1)} disabled={displayVal <= 0}
                                className="w-6 h-6 rounded bg-[#F4F6F5] flex items-center justify-center hover:bg-[#E1E6E4] disabled:opacity-30">
                                <Minus className="w-3 h-3" weight="bold" />
                              </button>
                              <span className={`w-7 text-center text-xs font-bold tabular-nums ${changed ? 'text-[#E59000]' : 'text-[#1D242B]'}`}>{displayVal}</span>
                              <button onClick={() => handleAdjust(key, 1)}
                                className="w-6 h-6 rounded bg-[#F4F6F5] flex items-center justify-center hover:bg-[#E1E6E4]">
                                <Plus className="w-3 h-3" weight="bold" />
                              </button>
                              {changed && (
                                <button onClick={() => revertEdit(key)} className="text-[8px] text-[#B8C7C0] hover:text-[#455561] underline ml-0.5">revert</button>
                              )}
                            </>
                          ) : (
                            <button onClick={() => startEdit(key, current)}
                              className="text-xs font-bold text-[#1D242B] tabular-nums hover:text-[#E59000] transition-colors px-1.5">
                              {current || '–'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
